import { ISelectedOption } from '../../app/@types/searchBox';

export interface IDuplicateCategoryAssessedItem {
  userId: number;
  templateCategoryId: number;
  userFirstName?: string;
  userLastName?: string;
}

export interface IDuplicateCategoryWithTaggedUsers {
  templateCategoryId?: number;
  templateCategoryUuid?: string;
  taggedUsers?: ISelectedOption[];
}

export interface IDuplicateCategoryAssessedPayload {
  userId: number;
  templateCategoryId: number;
}

export interface ISelfAssessmentWithTaggedUsers {
  duplicateCategories: IDuplicateCategoryWithTaggedUsers[];
  duplicateCategoryAssessed?: IDuplicateCategoryAssessedItem[];
}

export function hasHydratableDuplicateCategories (
  assessment: unknown
): assessment is ISelfAssessmentWithTaggedUsers {
  return (
    assessment != null &&
    typeof assessment === 'object' &&
    Array.isArray(
      (assessment as ISelfAssessmentWithTaggedUsers).duplicateCategories
    )
  );
}

function toSelectedOption (item: IDuplicateCategoryAssessedItem): ISelectedOption {
  return {
    id: item.userId,
    text: `${item.userFirstName || ''} ${item.userLastName || ''}`.trim(),
    userFirstName: item.userFirstName,
    userLastName: item.userLastName,
    templateCategoryId: item.templateCategoryId,
  };
}

export function hydrateSubDepartmentWithTaggedUsers (
  duplicateCategories: IDuplicateCategoryWithTaggedUsers[],
  duplicateCategoryAssessed: IDuplicateCategoryAssessedItem[] = []
): IDuplicateCategoryWithTaggedUsers[] {
  return duplicateCategories.map((duplicateCategory) => {
    const templateCategoryId = duplicateCategory.templateCategoryId;
    const taggedUsers = duplicateCategoryAssessed
      .filter((item) => item.templateCategoryId === templateCategoryId)
      .map(toSelectedOption);

    return {
      ...duplicateCategory,
      taggedUsers,
    };
  });
}

export function buildDuplicateCategoryAssessed (
  duplicateCategories: IDuplicateCategoryWithTaggedUsers[]
): IDuplicateCategoryAssessedPayload[] {
  const assessed: IDuplicateCategoryAssessedPayload[] = [];

  duplicateCategories.forEach((duplicateCategory) => {
    const templateCategoryId = duplicateCategory.templateCategoryId;
    if (templateCategoryId == null) {
      return;
    }

    (duplicateCategory.taggedUsers || []).forEach((option) => {
      const alreadyAdded = assessed.some(
        (entry) =>
          entry.userId === option.id &&
          entry.templateCategoryId === templateCategoryId
      );
      if (!alreadyAdded && typeof option.id === 'number') {
        assessed.push({
          userId: option.id,
          templateCategoryId,
        });
      }
    });
  });

  return assessed;
}

export function applyHydratedSelfAssessment<T extends object> (
  assessment: T
): T {
  if (!hasHydratableDuplicateCategories(assessment)) {
    return assessment;
  }

  return {
    ...assessment,
    duplicateCategories: hydrateSubDepartmentWithTaggedUsers(
      assessment.duplicateCategories,
      assessment.duplicateCategoryAssessed || []
    ),
  };
}
